import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { MarketProvider } from "./contexts/MarketContext";
import MarketWrapper from "./components/MarketWrapper";
import { Toaster } from "./components/ui/sonner";
import { initGA4, usePageTracking } from "./hooks/useAnalytics";
import MobileBottomNav from "./components/MobileBottomNav";

// Public Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ListingsPage from "./pages/ListingsPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProperties from "./pages/admin/AdminProperties";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAbout from "./pages/admin/AdminAbout";
import AdminListings from "./pages/admin/AdminListings";
import AdminServices from "./pages/admin/AdminServices";
import ContactProfile from "./pages/admin/ContactProfile";
import AdminReports from "./pages/admin/AdminReportsAndLogs";

// Reusable page routes (same pages used for every market)
const PublicPages = () => (
  <Routes>
    <Route index element={<HomePage />} />
    <Route path="services" element={<ServicesPage />} />
    <Route path="properties" element={<PropertiesPage />} />
    <Route path="properties/:propertyId" element={<PropertyDetailPage />} />
    <Route path="about" element={<AboutPage />} />
    <Route path="contact" element={<ContactPage />} />
    <Route path="listings" element={<ListingsPage />} />
    <Route path="listings/:listingId" element={<ListingDetailPage />} />
  </Routes>
);

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
};

const COUNTRY_MARKET_MAP = { BG: 'bg', GR: 'gr', CY: 'gr', TR: 'tr' };
const MARKET_PATHS = { bg: '/', gr: '/Greece', tr: '/Turkiye', en: '/Global' };

// IP-based auto-redirect on first visit (runs once per browser tab session)
function IpRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only act on the bare root path "/" - never interfere if user is
    // already on /gr, /tr, /en, /admin, /login, or any deep link
    if (location.pathname !== '/') return;

    // 1. If user made an explicit choice before → honor it
    const userChoice = localStorage.getItem('gc_market_choice');
    if (userChoice === 'bg') return; // user explicitly chose BG → stay
    if (userChoice && MARKET_PATHS[userChoice]) {
      navigate(MARKET_PATHS[userChoice], { replace: true });
      return;
    }

    // 2. Use sessionStorage (not localStorage) so detection re-runs in new
    //    sessions and is never blocked by stale flags from old code versions
    if (sessionStorage.getItem('gc_ip_checked')) return;

    // 3. First visit this session → detect country via IP
    fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) })
      .then(r => r.json())
      .then(data => {
        sessionStorage.setItem('gc_ip_checked', '1');
        // ipapi.co's free tier is a shared global quota (1000 req/day) and
        // returns HTTP 200 with { error: true, reason: "RateLimited" } once
        // exhausted - NOT a rejected fetch. Without this check, an
        // exhausted quota looks identical to "unrecognized country" below,
        // which defaults to the 'en' market - meaning every visitor,
        // including ones from Bulgaria, would get redirected to /en for
        // the rest of their session. Treat any API-reported error exactly
        // like a network failure: do nothing, stay on the default market.
        if (data?.error) return;
        const country = (data.country_code || data.country || '').toUpperCase();
        if (!country) return;
        const detected = COUNTRY_MARKET_MAP[country] || 'en';
        if (detected !== 'bg' && MARKET_PATHS[detected]) {
          navigate(MARKET_PATHS[detected], { replace: true });
        }
      })
      .catch(() => {
        sessionStorage.setItem('gc_ip_checked', '1');
      });
  }, [navigate, location.pathname]);

  return null;
}

// Redirect old short market paths (/gr, /tr, /en) to the new named ones
// (/Greece, /Turkiye, /Global), preserving any subpath and query string:
// /gr/properties/123 → /Greece/properties/123
function LegacyMarketRedirect({ to }) {
  const location = useLocation();
  const subpath = location.pathname.replace(/^\/(gr|tr|en)(?=\/|$)/i, '');
  return <Navigate to={`${to}${subpath}${location.search}`} replace />;
}

function AppRoutes() {
  usePageTracking();
  useEffect(() => { initGA4(); }, []);

  return (
    <>
      <IpRedirect />
      <Routes>
        {/* ── BG Market (default, no prefix) ─────────────── */}
        <Route path="/" element={<MarketWrapper market="bg" />}>
          <Route index element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:propertyId" element={<PropertyDetailPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="listings" element={<ListingsPage />} />
          <Route path="listings/:listingId" element={<ListingDetailPage />} />
        </Route>

        {/* ── GR Market (/Greece prefix) ──────────────────── */}
        <Route path="/Greece" element={<MarketWrapper market="gr" />}>
          <Route index element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:propertyId" element={<PropertyDetailPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="listings" element={<ListingsPage />} />
          <Route path="listings/:listingId" element={<ListingDetailPage />} />
        </Route>

        {/* ── TR Market (/Turkiye prefix) ─────────────────── */}
        <Route path="/Turkiye" element={<MarketWrapper market="tr" />}>
          <Route index element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:propertyId" element={<PropertyDetailPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="listings" element={<ListingsPage />} />
          <Route path="listings/:listingId" element={<ListingDetailPage />} />
        </Route>

        {/* ── EN Market (/Global prefix) ──────────────────── */}
        <Route path="/Global" element={<MarketWrapper market="en" />}>
          <Route index element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:propertyId" element={<PropertyDetailPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="listings" element={<ListingsPage />} />
          <Route path="listings/:listingId" element={<ListingDetailPage />} />
        </Route>

        {/* ── Legacy market paths → new names (preserve subpath) ── */}
        <Route path="/gr/*" element={<LegacyMarketRedirect to="/Greece" />} />
        <Route path="/gr" element={<LegacyMarketRedirect to="/Greece" />} />
        <Route path="/tr/*" element={<LegacyMarketRedirect to="/Turkiye" />} />
        <Route path="/tr" element={<LegacyMarketRedirect to="/Turkiye" />} />
        <Route path="/en/*" element={<LegacyMarketRedirect to="/Global" />} />
        <Route path="/en" element={<LegacyMarketRedirect to="/Global" />} />

        {/* ── Static pages ────────────────────────────────── */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* ── Auth & Admin (no market) ────────────────────── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager", "editor", "broker"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="properties" element={<ProtectedRoute allowedRoles={["admin","manager","editor"]}><AdminProperties /></ProtectedRoute>} />
          <Route path="listings" element={<ProtectedRoute allowedRoles={["admin","manager","editor","broker"]}><AdminListings /></ProtectedRoute>} />
          <Route path="contacts" element={<ProtectedRoute allowedRoles={["admin","manager"]}><AdminContacts /></ProtectedRoute>} />
          <Route path="contacts/:id" element={<ProtectedRoute allowedRoles={["admin","manager"]}><ContactProfile /></ProtectedRoute>} />
          <Route path="reports" element={<ProtectedRoute allowedRoles={["admin","manager"]}><AdminReports /></ProtectedRoute>} />
          <Route path="inquiries" element={<ProtectedRoute allowedRoles={["admin","manager","editor","broker"]}><AdminInquiries /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute allowedRoles={["admin","manager","editor"]}><AdminSettings /></ProtectedRoute>} />
          <Route path="about" element={<ProtectedRoute allowedRoles={["admin","manager","editor"]}><AdminAbout /></ProtectedRoute>} />
          <Route path="services" element={<ProtectedRoute allowedRoles={["admin","manager","editor"]}><AdminServices /></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute allowedRoles={["admin","manager","editor"]}><AdminUsers /></ProtectedRoute>} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <MarketProvider>
          <BrowserRouter>
            <AppRoutes />
            <MobileBottomNav />
            <Toaster position="bottom-right" />
          </BrowserRouter>
        </MarketProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
